import classNames from 'classnames';
import React, { ReactNode } from 'react';
import Empty from '@ui-kit/components/empty';
import styles from '../styles/dataTable.module.scss';
import { t } from '@base/utils/helpers';
import { Loader } from '@ui-kit/components/loader';

export type ColumnType<T> = {
  title: string;
  style?: React.CSSProperties;
  dataIndex: keyof T;
  render?: (data: T) => ReactNode;
};

export default function DataTable<T>(props: {
  data?: T[];
  columns: ColumnType<T>[];
  onRowClick?: (item: T) => void;
  className?: string;
  empty?: JSX.Element;
  style?: React.CSSProperties;
  loading?: boolean;
}) {
  return (
    <Table className={props.className} style={props.style}>
      <DataHeader columns={props.columns} />
      <tbody>
        {props.loading ? (
          <EmptyRow>
            <Loader />
          </EmptyRow>
        ) : props.data?.length ? (
          props.data.map((dataItem, index) => (
            <DataRow key={index} onRowClick={props.onRowClick} dataItem={dataItem}>
              {props.columns.map((col, index) => (
                <DataColumn dataItem={dataItem} col={col} key={index} />
              ))}
            </DataRow>
          ))
        ) : (
          <EmptyRow>{props.empty}</EmptyRow>
        )}
      </tbody>
    </Table>
  );
}

function Table(props: { className?: string; style?: React.CSSProperties; children: JSX.Element[] }) {
  return (
    <div className={classNames(styles.dataTable, props.className)} style={props.style}>
      <table>{props.children}</table>
    </div>
  );
}

function DataHeader<T>(props: { columns: ColumnType<T>[] }) {
  return (
    <thead>
      <tr key="tr">
        {props.columns.map((col, index) => (
          <HeaderColumn col={col} key={index} />
        ))}
      </tr>
    </thead>
  );
}

function HeaderColumn<T>(props: { col: ColumnType<T> }) {
  return <th style={{ ...props.col.style }}>{props.col.title ? t(props.col.title) : ''}</th>;
}

function DataRow<T>(props: { dataItem: T; onRowClick?: (item: T) => void; children: JSX.Element[] }) {
  return (
    <tr
      style={{ cursor: props.onRowClick ? 'pointer' : 'default' }}
      onClick={() => props.onRowClick?.(props.dataItem)}
    >
      {props.children}
    </tr>
  );
}

function DataColumn<T>(props: { dataItem: T; col: ColumnType<T> }) {
  return (
    <td style={{ ...props.col.style }}>
      {props.col.render ? props.col.render(props.dataItem) : props.dataItem[props.col.dataIndex]}
    </td>
  );
}

function EmptyRow(props: { children?: JSX.Element }) {
  return (
    <tr style={{ backgroundColor: '#387541', position: 'relative', height: '100%' }}>
      <td>{props.children || <Empty>No data</Empty>}</td>
    </tr>
  );
}
